'use client';

import Image from 'next/image';
import { useState, useEffect } from 'react';

import Lightbox from '../atoms/Lightbox';

const chunk = (arr, size) => {
  const res = [];
  for (let i = 0; i < arr.length; i += size) {
    res.push(arr.slice(i, i + size));
  }
  return res;
};

export default function MorePhotos({ photos = [] }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  if (!photos.length) return null;

  const visiblePhotos = photos.slice(0, visibleCount);
  const blocks = chunk(visiblePhotos, 9);

  const loadMore = () => {
    setVisibleCount(photos.length);
  };

  return (
    <>
      {isMobile && (
        <div className="md:px-4 lg:px-6 section-spacing relative">
          <h3 className="text-center md:text-base text-sm font-semibold mb-3">More Photos</h3>

          {blocks.map((block, blockIndex) => {
            const isBento = block.length === 9;
            const bigOnLeft = blockIndex % 2 === 0;

            // ✅ NORMAL GRID (<9)
            if (!isBento) {
              return (
                <div key={blockIndex} className="grid grid-cols-3 md:gap-3 sm:gap-2 gap-1.5">
                  {block.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveIndex(blockIndex * 9 + i)}
                      className="rounded-xl cursor-pointer overflow-hidden bg-gray-200 aspect-square"
                    >
                      <Image
                        src={img}
                        alt={`photo-${blockIndex * 9 + i}`}
                        width={325}
                        height={325}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              );
            }

            // BENTO GRID
            return (
              <div
                key={blockIndex}
                className="grid grid-cols-3 grid-rows-4 grid-flow-dense md:gap-3 sm:gap-2 gap-1.5 md:mb-3 sm:mb-2 mb-1.5"
              >
                {block.map((img, i) => {
                  const isBig = i === 6;
                  let placement = '';
                  if (isBig) {
                    placement = bigOnLeft
                      ? 'col-start-1 row-start-3 col-span-2 row-span-2'
                      : 'col-start-2 row-start-3 col-span-2 row-span-2';
                  }
                  return (
                    <button
                      key={i}
                      onClick={() => setActiveIndex(blockIndex * 9 + i)}
                      className={`rounded-xl cursor-pointer overflow-hidden bg-gray-200 aspect-square ${placement}`}
                    >
                      <Image
                        src={img}
                        alt={`photo-${blockIndex * 9 + i}`}
                        width={325}
                        height={325}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            );
          })}

          {visibleCount < photos.length && (
            <div className="w-full flex justify-center mt-4">
              <button
                onClick={loadMore}
                className="px-6 py-2 bg-gray-300 rounded-full text-gray-700 font-medium"
              >
                View All
              </button>
            </div>
          )}
        </div>
      )}

      <Lightbox
        images={photos}
        activeIndex={activeIndex}
        close={() => setActiveIndex(null)}
        goNext={() => setActiveIndex((p) => (p + 1) % photos.length)}
        goPrev={() => setActiveIndex((p) => (p === 0 ? photos.length - 1 : p - 1))}
        goTo={(i) => setActiveIndex(i)}
        key={activeIndex}
      />
    </>
  );
}
